import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragCancelEvent,
  DragOverlay,
  useSensors,
  useSensor,
  PointerSensor,
  rectIntersection
} from "@dnd-kit/core";
import { BoardType, ColumnType, CardType, CardCreatePayload, BoardMenuApplyResult } from "./types/index";
import {
  BoardWrapper,
  BoardContent,
  ErrorMessage,
  CardStyled,
  CardDescription,
  KanbanDragOverlayCardWrap,
} from './styles/index';
import { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from '../../styles/theme';
import BoardMenu from './components/BoardMenu';
import Column from './components/Column';
import KanbanSkeleton from './components/KanbanSkeleton';
import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import refreshToken from '../../utils/refreshToken';
import { useParams } from 'react-router-dom';
import Header from '../../components/Header';
import CardModal from './components/CardModal';
import MobileCardFullScreen from './components/MobileCardFullScreen';
import BottomMenuBar from './components/BottomMenuBar';
import { clearKanbanFilterPreferences } from './utils/kanbanFilterPreferences';
import CreateCardFromScheduleModal from './components/CreateCardFromScheduleModal';
import { userCanCreateKanbanCard } from '../../utils/permissions';

const DEBUG_KANBAN_DND = false;

function idNum(v: unknown): number {
  if (v === null || v === undefined) return NaN;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function sameId(a: unknown, b: unknown): boolean {
  return idNum(a) === idNum(b);
}

function KanbanBoard() {
  const { boardId: boardIdString } = useParams<{ boardId: string }>();
  const boardId = Number(boardIdString);
  const [board, setBoard] = useState<BoardType | null>(null);
  const [originalBoard, setOriginalBoard] = useState<BoardType | null>(null);
  const boardRef = useRef<BoardType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { access_token, dateToExpires, user } = useAuth();
  const userId = user?.id;
  const { addToast } = useToast();

  const [localTheme, setLocalTheme] = useState(lightTheme);
  const boardContentRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const [showMyFavorites, setShowMyFavorites] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCard, setSelectedCard] = useState<Partial<CardType> | null>(null);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isCreateFromScheduleOpen, setIsCreateFromScheduleOpen] = useState(false);
  const canCreateFromSchedule = userCanCreateKanbanCard(user);
  const [warningCardId, setWarningCardId] = useState<number | null>(null);
  const [activeDragCard, setActiveDragCard] = useState<CardType | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const ZOOM_OUT_LEVEL = 0.7;
  const [isZoomedOut, setIsZoomedOut] = useState(false);
  const zoomLevel = isZoomedOut ? ZOOM_OUT_LEVEL : 1;
  const handleZoomToggle = () => setIsZoomedOut(v => !v);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.body.style.background = localTheme.mode === 'dark' ? '#2A2A2A' : '#FFFFFF';
    return () => { document.body.style.background = ''; };
  }, [localTheme.mode]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFilterToggle = () => {
    setShowMyFavorites(!showMyFavorites);
  };

  const handleSearchTermChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleOpenCard = (card: CardType) => {
    setSelectedCard(card);
    setIsCardModalOpen(true);
  };

  const handleOpenNewCard = (columnId: number) => {
    const currentColumnName = board?.columns?.find(col => Number(col.id) === Number(columnId))?.name || '';
    const newCardTemplate: Partial<CardType> = {
      description: '',
      priority: 'important',
      column_id: columnId,
      ...(currentColumnName ? { column_name: currentColumnName } : {}),
      card_fields: [],
      favorites: [],
      myFavorites: false,
    };
    setSelectedCard(newCardTemplate);
    setIsCardModalOpen(true);
  };

  const handleCloseCardModal = () => {
    setIsCardModalOpen(false);
    setSelectedCard(null);
  };

  const handleThemeToggle = () => {
    const newTheme = localTheme.mode === 'light' ? darkTheme : lightTheme;
    setLocalTheme(newTheme);
    localStorage.setItem('kanbanTheme', newTheme.mode);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    if (!activeDragCard) return;
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [activeDragCard]);

  const fetchBoardData = async () => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token || access_token}`;
      const url = `/api/adm/boards/${boardId}`;
      const response = await api.get(url);
      const boardData: BoardType = response.data.data;

      const transformedColumns = boardData.columns.map(column => ({
        ...column,
        cards: {
          ...column.cards,
          data: column.cards.data.map(card => ({
            ...card,
            myFavorites: card.favorites?.some(fav => fav.id === userId) || false,
            card_fields: card.card_fields || [],
            created_at: card.created_at || card.createdAt
          })),
        },
      }));
      const transformedBoard = { ...boardData, columns: transformedColumns };
      setBoard(transformedBoard);
      setOriginalBoard(transformedBoard);
      boardRef.current = transformedBoard;
      setHasActiveFilters(false);
    } catch (error) {
      setError("Não foi possível carregar o quadro");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshBoard = async () => {
    await fetchBoardData();
    addToast({
      type: 'success',
      title: 'Board recarregado',
      description: 'Todos os cards foram atualizados.'
    });
  };

  const handleClearFilters = () => {
    clearKanbanFilterPreferences(boardId);
    if (originalBoard) {
      setBoard(originalBoard);
      boardRef.current = originalBoard;
      setHasActiveFilters(false);

      addToast({
        type: 'success',
        title: 'Filtros removidos',
        description: 'Board restaurado ao estado original.'
      });
    } else {
      handleRefreshBoard();
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('kanbanTheme');
    if (savedTheme === 'dark') {
      setLocalTheme(darkTheme);
    } else {
      setLocalTheme(lightTheme);
    }
    fetchBoardData();
  }, [boardId]);

  useLayoutEffect(() => {
    if (boardContentRef.current) {
      boardContentRef.current.scrollLeft = scrollPositionRef.current;
    }
  }, [board]);

  useEffect(() => {
    return () => {
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);

  const triggerRequiredFieldsWarning = (cardId: number, fieldLabels: string[]) => {
    const labelsPreview = fieldLabels.slice(0, 3).join(', ');
    const hasMore = fieldLabels.length > 3;

    setWarningCardId(cardId);
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    warningTimeoutRef.current = setTimeout(() => {
      setWarningCardId(null);
    }, 2800);

    addToast({
      type: 'error',
      title: 'Preencha os campos obrigatórios',
      description: hasMore
        ? `Campos pendentes: ${labelsPreview} e mais ${fieldLabels.length - 3}.`
        : `Campos pendentes: ${labelsPreview}.`,
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = Number(event.active.id);
    const b = boardRef.current;
    if (DEBUG_KANBAN_DND) {
      console.log('[Kanban DnD]', 'dragStart', { activeId: event.active.id, numericId: id, hasBoard: !!b });
    }
    if (!b) return;
    for (const col of b.columns) {
      const c = col.cards.data.find(card => sameId(card.id, id));
      if (c) {
        if (DEBUG_KANBAN_DND) {
          console.log('[Kanban DnD]', 'dragStart card', { cardId: c.id, column_id: col.id, column_name: col.name });
        }
        setActiveDragCard(c);
        return;
      }
    }
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    setActiveDragCard(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragCard(null);
    const { active, over } = event;
    const dndLog = (...args: unknown[]) => {
      if (DEBUG_KANBAN_DND) console.log('[Kanban DnD]', ...args);
    };

    if (!over || !boardRef.current) {
      dndLog('abort: sem destino (over) ou board', { over: over ?? null, hasBoardRef: !!boardRef.current });
      return;
    }
    const activeId = idNum(active.id);
    const overIdNum = idNum(over.id);
    let sourceColumnId: number | undefined;

    for (const column of boardRef.current.columns) {
      if (column.cards.data.find(card => sameId(card.id, activeId))) {
        sourceColumnId = idNum(column.id);
        break;
      }
    }

    if (sourceColumnId === undefined || Number.isNaN(sourceColumnId)) {
      dndLog('abort: card não encontrado no boardRef', { activeId, activeRaw: active.id });
      return;
    }

    const isOverColumn = boardRef.current.columns.some(col => sameId(col.id, over.id));
    const sortableContainerId = over.data.current?.sortable?.containerId;
    let targetColumnId: number | undefined;
    if (isOverColumn) {
      targetColumnId = overIdNum;
    } else {
      const fromContainer = idNum(sortableContainerId);
      if (!Number.isNaN(fromContainer)) {
        targetColumnId = fromContainer;
      } else if (sortableContainerId != null && sortableContainerId !== '') {
        const m = String(sortableContainerId).match(/^kanban-board-wrap-(\d+)$/);
        if (m) targetColumnId = idNum(m[1]);
      }
    }
    const sourceName = boardRef.current.columns.find(c => sameId(c.id, sourceColumnId))?.name;
    const targetName = boardRef.current.columns.find(c => sameId(c.id, targetColumnId))?.name;

    dndLog('drop', {
      activeId,
      overId: overIdNum,
      isOverColumn,
      sortableContainerId,
      targetColumnId,
      targetColumnIdValid: targetColumnId !== undefined && Number.isFinite(targetColumnId) && !Number.isNaN(targetColumnId),
      overSortable: over.data.current?.sortable,
      sourceColumnId,
      sourceName,
      targetName,
    });

    if (targetColumnId === undefined || Number.isNaN(targetColumnId)) {
      dndLog('abort: targetColumnId inválido — solta pode não ter batido na coluna/card certo (dnd-kit)');
      return;
    }

    const oldIndex = boardRef.current.columns
      .find(col => sameId(col.id, sourceColumnId))?.cards.data
      .findIndex(card => sameId(card.id, activeId));

    const targetColumn = boardRef.current.columns.find(col => sameId(col.id, targetColumnId));
    const newIndex = over.data.current?.sortable?.index ?? targetColumn?.cards.data.length ?? 0;

    if (sourceColumnId === targetColumnId && oldIndex === newIndex) {
      dndLog('abort: mesmo índice na mesma coluna (sem mudança)');
      return;
    }

    const columnOrder = boardRef.current.columns;
    const sourceColumnIndex = columnOrder.findIndex(c => sameId(c.id, sourceColumnId));
    const targetColumnIndex = columnOrder.findIndex(c => sameId(c.id, targetColumnId));
    const isMovingToEarlierColumn =
      sourceColumnIndex >= 0 &&
      targetColumnIndex >= 0 &&
      targetColumnIndex < sourceColumnIndex;

    if (sourceColumnId !== targetColumnId && isMovingToEarlierColumn) {
      dndLog('validação de obrigatórios ignorada: destino é coluna anterior no quadro', {
        sourceColumnIndex,
        targetColumnIndex,
      });
    }

    if (sourceColumnId !== targetColumnId && !isMovingToEarlierColumn) {
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token || access_token}`;

        const viewUrl = `/api/adm/cards/${activeId}/view`;
        dndLog('validação cross-column: GET', viewUrl);
        const viewResp = await api.get(viewUrl);
        const viewRoot = viewResp?.data as Record<string, unknown> | undefined;
        const viewData = (viewRoot?.data as Record<string, unknown> | undefined) ?? viewRoot;
        const sections = Array.isArray(viewData?.sections) ? viewData.sections : [];

        const allFields = sections.flatMap((section: any) =>
          Array.isArray(section?.fields) ? section.fields : []
        );
        const requiredFieldsDetail = allFields
          .filter((field: any) => Boolean(field?.required))
          .map((field: any) => {
            const raw = field?.value;
            const asString = String(raw ?? '').trim();
            const consideredEmpty = !asString;
            return {
              id: field?.id,
              label: field?.label,
              rawValue: raw,
              typeOfValue: typeof raw,
              stringTrimmed: asString,
              consideredEmpty,
            };
          });

        dndLog('campos obrigatórios (resposta /view)', requiredFieldsDetail);

        const emptyRequiredFields: string[] = sections
          .flatMap((section: any) => Array.isArray(section?.fields) ? section.fields : [])
          .filter((field: any) => Boolean(field?.required) && !String(field?.value ?? '').trim())
          .map((field: any) => String(field?.label || `Campo ${field?.id || ''}`));

        if (emptyRequiredFields.length > 0) {
          dndLog('BLOQUEADO: obrigatórios vazios segundo API', emptyRequiredFields);
          triggerRequiredFieldsWarning(activeId, emptyRequiredFields);
          return;
        }
        dndLog('validação OK: nenhum obrigatório vazio na resposta /view');
      } catch (validationError) {
        dndLog('ERRO na validação (GET /view) — fluxo continua para reordenar (catch vazio original)', validationError);
        if (validationError && typeof validationError === 'object' && 'response' in validationError) {
          const r = (validationError as { response?: { status?: number; data?: unknown } }).response;
          dndLog('axios response', { status: r?.status, data: r?.data });
        }
      }
    }

    if (boardContentRef.current) {
      scrollPositionRef.current = boardContentRef.current.scrollLeft;
    }

    const prevBoardState = { ...boardRef.current };
    const newColumns = prevBoardState.columns.map(col => ({ ...col, cards: { ...col.cards, data: [...col.cards.data] } }));

    const sourceColumn = newColumns.find(col => sameId(col.id, sourceColumnId));
    const targetColumnForUpdate = newColumns.find(col => sameId(col.id, targetColumnId));

    if (!sourceColumn || !targetColumnForUpdate || oldIndex === -1 || oldIndex === undefined) {
      if (DEBUG_KANBAN_DND) {
        console.log('[Kanban DnD] abort: splice inválido', {
          hasSource: !!sourceColumn,
          hasTarget: !!targetColumnForUpdate,
          oldIndex,
        });
      }
      return;
    }

    const [movedItem] = sourceColumn.cards.data.splice(oldIndex, 1);
    const cardToMove: CardType = { ...movedItem, column_id: targetColumnId };
    targetColumnForUpdate.cards.data.splice(newIndex, 0, cardToMove);

    if (DEBUG_KANBAN_DND) {
      console.log('[Kanban DnD]', 'stateAfterSplice', {
        oldIndex,
        newIndex,
        sourceColumnId,
        targetColumnId,
        sameColumn: sourceColumnId === targetColumnId,
        movedCardId: movedItem.id,
        cardToMove_column_id: cardToMove.column_id,
        sourceColumnCardIds: sourceColumn.cards.data.map(c => c.id),
        targetColumnCardIds: targetColumnForUpdate.cards.data.map(c => c.id),
      });
    }

    const updatedBoard = { ...prevBoardState, columns: newColumns };
    setBoard(updatedBoard);
    boardRef.current = updatedBoard;

    try {
      const latestBoard = boardRef.current;
      if (!latestBoard) return;
      const modifiedColumns = latestBoard.columns.filter(
        col => sameId(col.id, sourceColumnId) || sameId(col.id, targetColumnId)
      );
      const reorderPayload = {
        columns: modifiedColumns.map(column => ({
          column_id: column.id,
          cards: column.cards.data.map((card, index) => ({ id: card.id, position: index }))
        })).filter(column => column.cards.length > 0)
      };

      console.groupCollapsed('[Kanban reorder] enviando PUT /api/adm/cards/reorder');
      console.log('contexto', {
        boardId: latestBoard.id,
        sourceColumnId,
        targetColumnId,
        hasActiveFilters,
        columnIdsNoPayload: modifiedColumns.map(c => c.id),
        totalCardsPorColuna: modifiedColumns.map(c => ({
          column_id: c.id,
          count: c.cards.data.length,
          ids: c.cards.data.map(card => card.id),
        })),
      });
      console.log('payload (objeto)', reorderPayload);
      console.log('payload (JSON)', JSON.stringify(reorderPayload, null, 2));
      console.groupEnd();

      if (DEBUG_KANBAN_DND) {
        console.log('[Kanban DnD]', 'PUT /api/adm/cards/reorder', reorderPayload);
      }
      await api.put(`/api/adm/cards/reorder`, reorderPayload);
      if (DEBUG_KANBAN_DND) console.log('[Kanban DnD] reorder OK');
      addToast({ type: "success", title: "Cards reordenados!" });
    } catch (err) {
      const ax = err as {
        message?: string;
        response?: { status?: number; statusText?: string; data?: Record<string, unknown> };
      };
      console.groupCollapsed('[Kanban reorder] falhou — inspecione response.data');
      console.error('axios message:', ax?.message);
      console.error('response.status:', ax?.response?.status, ax?.response?.statusText);
      console.error('response.data (completo):', ax?.response?.data);
      const data = ax?.response?.data;
      if (data && typeof data === 'object') {
        if ('message' in data) console.error('message (API):', data.message);
        if ('errors' in data) console.error('errors (validação Laravel etc.):', data.errors);
      }
      console.groupEnd();

      if (DEBUG_KANBAN_DND) {
        console.log('[Kanban DnD] reorder FALHOU', err);
        if (err && typeof err === 'object' && 'response' in err) {
          const r = (err as { response?: { status?: number; data?: unknown } }).response;
          console.log('[Kanban DnD] reorder axios', { status: r?.status, data: r?.data });
        }
      }
      if (prevBoardState) {
        setBoard(prevBoardState as BoardType);
        boardRef.current = prevBoardState as BoardType;
      }
      const apiData = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data
        : undefined;
      const serverMsg = typeof apiData?.message === 'string' && apiData.message.trim()
        ? apiData.message.trim()
        : null;
      addToast({
        type: "error",
        title: "Erro ao reordenar cards",
        description: serverMsg ?? "Não foi possível salvar a nova ordem no servidor.",
      });
    }
  };

  const handleCardUpdate = (cardToUpdate: CardType) => {
    setBoard(prevBoard => {
      if (!prevBoard) return null;

      const normalizeId = (v: any) => (v === null || v === undefined ? v : Number(v));
      const updatedCardId = normalizeId((cardToUpdate as any).id);
      const updatedColumnId = normalizeId((cardToUpdate as any).column_id);

      let cardExists = false;

      const newColumns = prevBoard.columns.map(column => {
        const cardIndex = column.cards.data.findIndex(c => normalizeId(c.id) === updatedCardId);

        if (cardIndex > -1) {
          cardExists = true;
          const newCards = [...column.cards.data];
          newCards[cardIndex] = {
            ...cardToUpdate,
            card_fields: cardToUpdate.card_fields || [],
            created_at: cardToUpdate.created_at || cardToUpdate.createdAt
          };
          return { ...column, cards: { ...column.cards, data: newCards } };
        }
        return column;
      });

      if (!cardExists && updatedColumnId) {
        const targetColumnIndex = newColumns.findIndex(col => normalizeId(col.id) === updatedColumnId);
        if (targetColumnIndex > -1) {
          const newCards = [...newColumns[targetColumnIndex].cards.data];
          newCards.push({
            ...cardToUpdate,
            card_fields: cardToUpdate.card_fields || [],
            created_at: cardToUpdate.created_at || cardToUpdate.createdAt
          });
          newColumns[targetColumnIndex] = {
            ...newColumns[targetColumnIndex],
            cards: { ...newColumns[targetColumnIndex].cards, data: newCards }
          };
        }
      }

      const updatedBoard = { ...prevBoard, columns: newColumns };
      boardRef.current = updatedBoard;
      return updatedBoard;
    });
  };

  const handleApplyFilters = useCallback((result: BoardMenuApplyResult) => {
    const snapshot = boardRef.current;
    if (!snapshot) return;

    setOriginalBoard(orig => orig ?? snapshot);

    const filteredCards = Array.isArray(result?.cards) ? result.cards : [];

    setBoard(prevBoard => {
      if (!prevBoard) return null;

      const newColumns = prevBoard.columns.map(column => {
        const cardsInColumn = filteredCards.filter(
          (card: any) => card && Number(card.column_id) === Number(column.id)
        );

        return {
          ...column,
          cards: {
            ...column.cards,
            data: cardsInColumn.map((card: any) => ({
              ...card,
              card_fields: card.card_fields || [],
              myFavorites: card.favorites?.some((fav: any) => fav?.id === userId) || false,
              created_at: card.created_at || card.createdAt
            }))
          }
        };
      });

      const updatedBoard = {
        ...prevBoard,
        columns: newColumns
      };

      boardRef.current = updatedBoard;
      return updatedBoard;
    });

    setHasActiveFilters(true);

    addToast({
      type: filteredCards.length > 0 ? 'success' : 'info',
      title: filteredCards.length > 0 ? 'Filtros aplicados!' : 'Nenhum card encontrado',
      description: filteredCards.length > 0
        ? `${filteredCards.length} cards encontrados`
        : 'Tente outros filtros ou limpe os filtros aplicados.'
    });
  }, [userId, addToast]);

  const onAddCard = async (newCardPayload: CardCreatePayload): Promise<CardType> => {
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token || access_token}`;
      const response = await api.post('/api/adm/cards', newCardPayload);

      const newCard = response.data.data;

      addToast({
        type: 'success',
        title: 'Card criado com sucesso!',
        description: `Card "${newCard.description || 'Sem título'}" foi adicionado ao quadro.`
      });

      await fetchBoardData();

      return newCard;
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao criar card',
        description: 'Tente novamente mais tarde.'
      });
      throw error;
    }
  };

  const filterCardsBySearch = (cards: CardType[]) => {
    if (!searchTerm) return cards;
    const lowerSearch = searchTerm.toLowerCase();
    return cards.filter(card =>
      card.description?.toLowerCase().includes(lowerSearch)
    );
  };

  return (
    <ThemeProvider theme={localTheme.mode === 'dark' ? darkTheme : lightTheme}>
      <>
        <Header />
        <BoardWrapper>
          {isLoading && <KanbanSkeleton />}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {!isLoading && !error && board && (
            <>
              <BottomMenuBar
                boardId={board.id}
                onThemeToggle={handleThemeToggle}
                onMyCardsToggle={handleFilterToggle}
                onSearchTermChange={handleSearchTermChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                showMyCards={showMyFavorites}
                currentTheme={localTheme.mode}
                onZoomToggle={handleZoomToggle}
                isZoomedOut={isZoomedOut}
                hasActiveFilters={hasActiveFilters}
                canCreateFromSchedule={canCreateFromSchedule}
                onOpenCreateFromSchedule={() => setIsCreateFromScheduleOpen(true)}
              />

              <BoardMenu
                boardTitle={board.name}
                boardName={board.name}
                boardId={board.id}
                currentTheme={localTheme.mode}
                onThemeToggle={handleThemeToggle}
                showMyCards={showMyFavorites}
                onMyCardsToggle={handleFilterToggle}
                onSearchTermChange={handleSearchTermChange}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                onRefreshBoard={handleRefreshBoard}
                hasActiveFilters={hasActiveFilters}
                canCreateFromSchedule={canCreateFromSchedule}
                onOpenCreateFromSchedule={() => setIsCreateFromScheduleOpen(true)}
              />
              <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}
              >
                <BoardContent
                  ref={boardContentRef}
                  style={isMobile && zoomLevel < 1 ? {
                    zoom: zoomLevel,
                    width: `calc(100% / ${zoomLevel})`,
                    height: `calc((100vh - 126px) / ${zoomLevel})`,
                    transition: 'zoom 0.2s ease',
                  } : undefined}
                >
                  {board?.columns?.map((column, index) => {
                    const myCardsFiltered = showMyFavorites
                      ? column.cards.data.filter(card => card.myFavorites || card.user_id === userId)
                      : column.cards.data;

                    const finalCards = filterCardsBySearch(myCardsFiltered);

                    return (
                      <Column
                        key={column.id}
                        column={{ ...column, cards: { ...column.cards, data: finalCards } }}
                        boardId={board.id}
                        onOpenCard={handleOpenCard}
                        onOpenNewCard={handleOpenNewCard}
                        onOpenCreateFromSchedule={() => setIsCreateFromScheduleOpen(true)}
                        canCreateFromSchedule={canCreateFromSchedule}
                        columnIndex={index}
                        warningCardId={warningCardId}
                        zoomLevel={isMobile ? zoomLevel : undefined}
                      />
                    );
                  })}
                </BoardContent>
                <DragOverlay
                  dropAnimation={{
                    duration: 220,
                    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                  }}
                >
                  {activeDragCard ? (
                    <KanbanDragOverlayCardWrap>
                      <CardStyled
                        $isOverlay
                        $priority={activeDragCard.priority}
                        $warning={warningCardId === activeDragCard.id}
                      >
                        <CardDescription>
                          {activeDragCard.description || 'Sem descrição'}
                        </CardDescription>
                      </CardStyled>
                    </KanbanDragOverlayCardWrap>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </>
          )}

          {isCardModalOpen && selectedCard && (
            isMobile ? (
              <MobileCardFullScreen
                isOpen={isCardModalOpen}
                onClose={handleCloseCardModal}
                card={selectedCard}
                onSave={handleCardUpdate}
                onCreate={onAddCard}
                boardId={boardId}
                title={selectedCard.description || undefined}
              />
            ) : (
              <CardModal
                isOpen={isCardModalOpen}
                onClose={handleCloseCardModal}
                card={selectedCard}
                onSave={handleCardUpdate}
                onCreate={onAddCard}
                boardId={boardId}
              />
            )
          )}

          {isCreateFromScheduleOpen && (
            <CreateCardFromScheduleModal
              isOpen={isCreateFromScheduleOpen}
              onClose={() => setIsCreateFromScheduleOpen(false)}
              onCreated={fetchBoardData}
            />
          )}

          {!isLoading && !error && !board && (
            <ErrorMessage>Nenhum quadro encontrado.</ErrorMessage>
          )}
        </BoardWrapper>
      </>
    </ThemeProvider>
  );
}

export default KanbanBoard;